/**
 * Login Overlay Web Component
 *
 * Features:
 * - 4 views: initial, login, signup, forgot-password, forgot-password-success
 * - Client-side validation
 * - Password visibility toggle
 * - Email carry-over between views
 * - Loading states on form submit
 */

if (!customElements.get('login-overlay')) {
  customElements.define(
    'login-overlay',
    class LoginOverlay extends HTMLElement {
      constructor() {
        super();
        this.currentView = 'initial';
        this.email = '';
      }

      connectedCallback() {
        this.bindEvents();
      }

      bindEvents() {
        // Overlay open/close events from body-controller
        document.addEventListener('overlay:request-open', this.onRequestOpen.bind(this));
        document.addEventListener('overlay:request-close-all', this.onRequestCloseAll.bind(this));

        // Close buttons and backdrop
        this.querySelectorAll('[data-close-overlay]').forEach((el) => {
          el.addEventListener('click', this.close.bind(this));
        });

        // View switching actions
        this.querySelectorAll('[data-action]').forEach((el) => {
          el.addEventListener('click', this.handleAction.bind(this));
        });

        // Password toggle buttons
        this.querySelectorAll('[data-toggle-password]').forEach((el) => {
          el.addEventListener('click', this.togglePassword.bind(this));
        });

        // Form submissions
        this.querySelectorAll('form').forEach((form) => {
          form.addEventListener('submit', this.handleFormSubmit.bind(this));
        });

        // Real-time validation on input
        this.querySelectorAll('input').forEach((input) => {
          input.addEventListener('blur', () => this.validateField(input));
          input.addEventListener('input', () => this.clearFieldError(input));
        });

        // Intercept links to /account/login
        document.addEventListener('click', this.interceptLoginLinks.bind(this));
      }

      interceptLoginLinks(event) {
        const link = event.target.closest('a[href*="/account/login"]');
        if (!link) return;

        // Don't intercept if customer is logged in (link would go to /account instead)
        const href = link.getAttribute('href');
        if (href && (href.includes('/account/login') || href === window.routes?.account_login_url)) {
          event.preventDefault();
          this.open();
        }
      }

      onRequestOpen(event) {
        const { overlayId } = event.detail || {};
        if (overlayId === this.id) {
          this.open();
        }
      }

      onRequestCloseAll() {
        if (this.hasAttribute('open')) {
          this.close();
        }
      }

      open() {
        this.setAttribute('open', '');
        this.showView('initial');

        // Dispatch event to body-controller
        document.dispatchEvent(
          new CustomEvent('overlay:open', {
            detail: { hash: this.dataset.overlayHash, overlayId: this.id },
          })
        );

        // Focus first input
        requestAnimationFrame(() => {
          const firstInput = this.querySelector('[data-view="initial"]:not([hidden]) input');
          if (firstInput) firstInput.focus();
        });
      }

      close() {
        this.removeAttribute('open');

        // Dispatch event to body-controller
        document.dispatchEvent(
          new CustomEvent('overlay:close', {
            detail: { overlayId: this.id },
          })
        );

        // Reset forms and views
        this.resetForms();
      }

      resetForms() {
        this.querySelectorAll('form').forEach((form) => form.reset());
        this.querySelectorAll('.login-overlay__field-error').forEach((el) => (el.textContent = ''));
        this.querySelectorAll('.login-overlay__field--error').forEach((el) =>
          el.classList.remove('login-overlay__field--error')
        );
        this.querySelectorAll('.login-overlay__button').forEach((btn) => btn.classList.remove('is-loading'));
        this.email = '';
      }

      showView(viewName) {
        this.currentView = viewName;

        // Hide all views
        this.querySelectorAll('[data-view]').forEach((view) => {
          view.hidden = true;
        });

        // Show target view
        const targetView = this.querySelector(`[data-view="${viewName}"]`);
        if (targetView) {
          targetView.hidden = false;

          // Pre-fill email if available
          const emailInput = targetView.querySelector('input[type="email"]');
          if (emailInput && this.email) {
            emailInput.value = this.email;
          }

          // Focus first input
          requestAnimationFrame(() => {
            const firstInput = targetView.querySelector('input:not([type="hidden"])');
            if (firstInput) firstInput.focus();
          });
        }
      }

      handleAction(event) {
        const action = event.currentTarget.dataset.action;

        switch (action) {
          case 'show-signup':
            this.captureEmail();
            this.showView('signup');
            break;
          case 'show-login':
            this.captureEmail();
            this.showView('login');
            break;
          case 'show-forgot-password':
            this.captureEmail();
            this.showView('forgot-password');
            break;
          case 'show-initial':
            this.captureEmail();
            this.showView('initial');
            break;
        }
      }

      captureEmail() {
        const currentViewEl = this.querySelector(`[data-view="${this.currentView}"]`);
        if (currentViewEl) {
          const emailInput = currentViewEl.querySelector('input[type="email"]');
          if (emailInput && emailInput.value) {
            this.email = emailInput.value;
          }
        }
      }

      togglePassword(event) {
        const button = event.currentTarget;
        const field = button.closest('.login-overlay__field');
        const input = field.querySelector('input');
        const showIcon = button.querySelector('[data-icon="show"]');
        const hideIcon = button.querySelector('[data-icon="hide"]');

        if (input.type === 'password') {
          input.type = 'text';
          showIcon.hidden = true;
          hideIcon.hidden = false;
          button.setAttribute('aria-label', button.getAttribute('aria-label').replace('Show', 'Hide'));
        } else {
          input.type = 'password';
          showIcon.hidden = false;
          hideIcon.hidden = true;
          button.setAttribute('aria-label', button.getAttribute('aria-label').replace('Hide', 'Show'));
        }
      }

      handleFormSubmit(event) {
        const form = event.target;
        const formType = form.dataset.form;

        // Client-side validation
        if (!this.validateForm(form)) {
          event.preventDefault();
          return;
        }

        // Special handling for initial form (not a real Shopify form)
        if (formType === 'initial') {
          event.preventDefault();
          this.captureEmail();
          this.showView('login');
          return;
        }

        // Special handling for forgot-password (show success view after submit)
        if (formType === 'forgot-password') {
          event.preventDefault();
          this.handleForgotPasswordSubmit(form);
          return;
        }

        // For login and signup, let Shopify handle the form but show loading state
        const submitButton = form.querySelector('button[type="submit"]');
        if (submitButton) {
          submitButton.classList.add('is-loading');
          submitButton.disabled = true;
        }
      }

      async handleForgotPasswordSubmit(form) {
        const submitButton = form.querySelector('button[type="submit"]');
        if (submitButton) {
          submitButton.classList.add('is-loading');
          submitButton.disabled = true;
        }

        try {
          const formData = new FormData(form);
          const response = await fetch(form.action, {
            method: 'POST',
            body: formData,
          });

          // Show success view regardless of response (for security - don't reveal if email exists)
          this.showView('forgot-password-success');
        } catch (error) {
          console.error('Password reset request failed:', error);
          // Still show success view for security
          this.showView('forgot-password-success');
        } finally {
          if (submitButton) {
            submitButton.classList.remove('is-loading');
            submitButton.disabled = false;
          }
        }
      }

      validateForm(form) {
        const inputs = form.querySelectorAll('input[required], input[type="email"], input[minlength]');
        let isValid = true;

        inputs.forEach((input) => {
          if (!this.validateField(input)) {
            isValid = false;
          }
        });

        // Special validation for terms checkbox in signup
        const termsCheckbox = form.querySelector('input[name="terms_accepted"]');
        if (termsCheckbox && !termsCheckbox.checked) {
          this.showFieldError(termsCheckbox, 'You must accept the Terms & Conditions');
          isValid = false;
        }

        return isValid;
      }

      validateField(input) {
        const field = input.closest('.login-overlay__field, .login-overlay__checkbox');
        if (!field) return true;

        let errorMessage = '';

        // Required validation
        if (input.required && !input.value.trim()) {
          errorMessage = 'This field is required';
        }

        // Email validation
        if (input.type === 'email' && input.value) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(input.value)) {
            errorMessage = 'Please enter a valid email address';
          }
        }

        // Minimum length validation
        if (input.minLength && input.value && input.value.length < input.minLength) {
          errorMessage = `Must be at least ${input.minLength} characters`;
        }

        if (errorMessage) {
          this.showFieldError(input, errorMessage);
          return false;
        } else {
          this.clearFieldError(input);
          return true;
        }
      }

      showFieldError(input, message) {
        const field = input.closest('.login-overlay__field, .login-overlay__checkbox');
        if (!field) return;

        field.classList.add('login-overlay__field--error');

        const errorEl = field.querySelector('.login-overlay__field-error');
        if (errorEl) {
          errorEl.textContent = message;
        }
      }

      clearFieldError(input) {
        const field = input.closest('.login-overlay__field, .login-overlay__checkbox');
        if (!field) return;

        field.classList.remove('login-overlay__field--error');

        const errorEl = field.querySelector('.login-overlay__field-error');
        if (errorEl) {
          errorEl.textContent = '';
        }
      }
    }
  );
}
